import { CommonModule, DOCUMENT } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { inject, ModuleWithProviders, NgModule, provideAppInitializer } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { LocalizationService } from '@cm/core/localization';
import { CmEventService } from '@cm/core/system';
import {
	CmAuthGuard,
	CmCrcatTypeGuard,
	CmCreqTypeGuard,
	CmFeatureGuard,
	CmIntentGuard,
	CmLocalizationGuard,
	CmPublicFeaturesGuard,
	CmUnsavedChangesGuard,
} from './guards';
import { AuthRedirectGuard } from './guards/auth-redirect.guard';
import { formErrorFocusInitializerFactory } from './initializers';
import { CmAuthTokenInterceptor, CmLoadingInterceptor, CmLoggingInterceptor } from './interceptors';
import { CoreConfig } from './models';
import { CmBooleanPipe, CmLocalizePipe } from './pipes';
import {
	CmAuthService,
	CmCookiePolicyService,
	CmFeatureService,
	CmFormErrorService,
	CmLocalizationService,
	CmMissingLocalizationService,
	CmRouterErrorService,
	CmSettingsService,
	CmSSOService,
	CmThemeService,
	CmTimeoutService,
} from './services';
import { CORE_CONFIG_TOKEN, LOC_PORTAL_TYPE } from './tokens';

@NgModule({
	imports: [CommonModule, RouterModule, MatDialogModule],
	declarations: [CmLocalizePipe, CmBooleanPipe],
	exports: [CmLocalizePipe, CmBooleanPipe],
})
export class CmCoreModule {
	constructor(private readonly routerErrors: CmRouterErrorService) {}

	static forRoot(config: CoreConfig): ModuleWithProviders<CmCoreModule> {
		return {
			ngModule: CmCoreModule,
			providers: [
				CmAuthGuard,
				AuthRedirectGuard,
				CmAuthService,
				CmThemeService,
				CmFeatureService,
				CmFormErrorService,
				CmSSOService,
				CmLocalizationService,
				CmLocalizePipe,
				CmBooleanPipe,
				CmMissingLocalizationService,
				CmCrcatTypeGuard,
				CmPublicFeaturesGuard,
				CmFeatureGuard,
				CmSettingsService,
				CmCreqTypeGuard,
				CmUnsavedChangesGuard,
				CmCookiePolicyService,
				CmRouterErrorService,
				CmLocalizationGuard,
				CmIntentGuard,
				{
					provide: HTTP_INTERCEPTORS,
					useClass: CmLoadingInterceptor,
					multi: true,
					deps: [CmEventService],
				},
				{
					provide: HTTP_INTERCEPTORS,
					useClass: CmAuthTokenInterceptor,
					multi: true,
					deps: [CmAuthService, CmSettingsService, Router, CmTimeoutService],
				},
				{
					provide: HTTP_INTERCEPTORS,
					useClass: CmLoggingInterceptor,
					multi: true,
					deps: [CmEventService, LocalizationService, Router],
				},
				{ provide: CORE_CONFIG_TOKEN, useValue: config },
				{ provide: LOC_PORTAL_TYPE, useValue: config.portal },
				provideAppInitializer(() => {
					const initializerFn = formErrorFocusInitializerFactory(inject(DOCUMENT));
					return initializerFn();
				}),
			],
		};
	}
}
